import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { site_url } from './../config.js';
import Sidebar from './../Sidebar';
import Footer from './../Footer';
import './index.css';
import { AuthContext } from './../AuthContext';
import Swal from 'sweetalert2';


function DenominationCal() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const sId = params.get('id');
  const sales_amount = params.get('sales_amount');
  const toDate = params.get('toDate');
  const denominationId = params.get('denomination_id');
  const date = params.get('date');
  const isEditMode = params.get('edit') === 'true';
  const { user, refreshUser, vendordetails } = useContext(AuthContext);

  const [note500, setNote500] = useState(0);
  const [note200, setNote200] = useState(0);
  const [note100, setNote100] = useState(0);
  const [note50, setNote50] = useState(0);
  const [note20, setNote20] = useState(0);
  const [note10, setNote10] = useState(0);
  const [coins, setCoins] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [requiredAmount, setRequiredAmount] = useState(0);

  const totalCash = useMemo(() => (
    (Number(note500) || 0) * 500 +
    (Number(note200) || 0) * 200 +
    (Number(note100) || 0) * 100 +
    (Number(note50) || 0) * 50 +
    (Number(note20) || 0) * 20 +
    (Number(note10) || 0) * 10 +
    (Number(coins) || 0)
  ), [note500, note200, note100, note50, note20, note10, coins]);


  const resetAll = () => {
    // Don't allow reset if already saved
    if (isSaved) {
      Swal.fire({
        title: 'Cannot Reset!',
        text: 'Denomination has already been saved. Please contact admin for any changes.',
        icon: 'warning',
        confirmButtonText: 'OK',
      });
      return;
    }
    setNote500(0);
    setNote200(0);
    setNote100(0);
    setNote50(0);
    setNote20(0);
    setNote10(0);
    setCoins(0);
    // setRequiredPayment(0);
  };

  // Helper function to handle integer-only input
  const handleIntegerInput = (value, setter) => {
    // Remove any non-digit characters except empty string
    // Also handles paste operations and negative signs, decimals, etc.
    const intValue = value === '' ? '' : value.toString().replace(/[^0-9]/g, '');
    setter(intValue === '' ? '' : intValue);
  };

  useEffect(() => {
    const fetchOverallTotal = async () => {
      try {
        // If editing a specific denomination, fetch it directly
        if (isEditMode && denominationId) {
          const res = await axios.get(`${site_url}/edit_denomination/${denominationId}`);
          if (res.data && res.data.status === 'success' && res.data.denomination) {
            const denom = res.data.denomination;
            setNote500(denom['500amount'] || 0);
            setNote200(denom['200amount'] || 0);
            setNote100(denom['100amount'] || 0);
            setNote50(denom['50amount'] || 0);
            setNote20(denom['20amount'] || 0);
            setNote10(denom['10amount'] || 0);
            setCoins(denom['coinsamount'] || 0);
            setIsSaved(false); // Allow editing
            return;
          }
        }

        // Fetch overall total based on w_id and date (not route-based)
        if (!user || !user.id) return;
        const selectedDate = date || toDate || new Date().toISOString().split('T')[0];
        const res = await axios.post(site_url + '/get-overall-total-denomination', { 
          w_id: user.id, 
          date: selectedDate 
        });
        if (res.data && res.data.status === 'success') {
          // Set required amount (today's total collection)
          if (res.data.total_amount) {
            setRequiredAmount(Number(res.data.total_amount) || 0);
          }
          // If existing denomination data exists, populate the form
          if (res.data.denomination) {
            setNote500(res.data.denomination['500amount'] || 0);
            setNote200(res.data.denomination['200amount'] || 0);
            setNote100(res.data.denomination['100amount'] || 0);
            setNote50(res.data.denomination['50amount'] || 0);
            setNote20(res.data.denomination['20amount'] || 0);
            setNote10(res.data.denomination['10amount'] || 0);
            setCoins(res.data.denomination['coinsamount'] || 0);
            setIsSaved(true);
          }
        }
      } catch (e) {
        console.error('Error fetching denomination:', e);
      }
    };
    if (user && user.id) {
      fetchOverallTotal();
    }
  }, [user, denominationId, isEditMode, date, toDate]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // If already saved and not in edit mode, show message and return
    if (isSaved && !isEditMode) {
      Swal.fire({
        title: 'Already Saved!',
        text: 'Denomination has already been saved. Please contact admin for any changes.',
        icon: 'info',
        confirmButtonText: 'OK',
      });
      return;
    }

    
    
    // if (!sId && !denominationId) {
    //   Swal.fire({
    //     title: 'Error!',
    //     text: 'Route ID is missing',
    //     icon: 'error',
    //     confirmButtonText: 'OK',
    //   });
    //   return;
    // }
    
    if (!user || !user.id) {
      Swal.fire({
        title: 'Error!',
        text: 'User information is missing',
        icon: 'error',
        confirmButtonText: 'OK',
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const denominationData = {
        w_id: user.id,
        '500amount': note500.toString(),
        '200amount': note200.toString(),
        '100amount': note100.toString(),
        '50amount': note50.toString(),
        '20amount': note20.toString(),
        '10amount': note10.toString(),
        'coinsamount': coins.toString(),
        totalcollectionamount: totalCash.toString(),
        collectiondate: date || toDate || new Date().toISOString().split('T')[0]
      };
      
      let response;
      
      // If editing, use update endpoint
      if (isEditMode && denominationId) {
        denominationData.id = denominationId;
        response = await axios.post(site_url + '/update_denomination', denominationData);
      } else {
        // Otherwise, use save endpoint
        response = await axios.post(site_url + '/save-denomination', denominationData);
      }
      
      if (response.data && response.data.status === 'success') {
        setIsSaved(true); // Mark as saved
        Swal.fire({
          title: 'Success!',
          text: response.data.message || (isEditMode ? 'Denomination updated successfully' : 'Denomination saved successfully'),
          icon: 'success',
          confirmButtonText: 'OK',
        }).then(() => {
          // Navigate back to list after saving/updating
          if (isEditMode) {
            window.location.href = '/Denominationlist';
          }
        });
      } else {
        throw new Error(response.data?.message || 'Failed to save denomination');
      }
    } catch (error) {
      console.error('Error saving denomination:', error);
      Swal.fire({
        title: 'Error!',
        text: error.response?.data?.message || error.message || 'Failed to save denomination',
        icon: 'error',
        confirmButtonText: 'OK',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveButtonClick = (e) => {
    // If button is disabled due to already being saved, show message
    if (isSaved) {
      e.preventDefault();
      Swal.fire({
        title: 'Already Saved!',
        text: 'Denomination has already been saved. Please contact admin for any changes.',
        icon: 'info',
        confirmButtonText: 'OK',
      });
    }
  };

  return (
    <div className="container p-3">
      <div className="d-flex align-items-center justify-content-between mt-5">
        <h3 className="mb-0">Denomination Calculation</h3>
        <div className="d-flex gap-2">
          <button className="btn btn-outline-secondary" onClick={resetAll} disabled={isSubmitting || isSaved} title={isSaved ? 'Cannot reset after saving' : ''}>Reset</button>
          <button 
            className={`btn ${isSaved ? 'btn-secondary' : 'btn-primary'}`}
            onClick={isSaved ? handleSaveButtonClick : handleSubmit}
            disabled={isSubmitting || isSaved}
            style={isSaved ? { cursor: 'pointer', opacity: 0.7 } : {}}
            title={isSaved ? 'Already saved. Click to contact admin.' : ''}
          >
            {isSubmitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Saving...
              </>
            ) : isSaved ? (
              'Already Saved'
            ) : (
              'Save Denomination'
            )}
          </button>
        </div>
      </div>

      <div className="row mt-3 g-3">
        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-bordered table-sm mb-0 align-middle">
                  <thead className="table-light">
                    <tr>
                      <th style={{width:'30%'}}>Denomination</th>
                      <th style={{width:'35%'}}>Count</th>
                      <th style={{width:'35%'}}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><b>₹ 500</b></td>
                      <td>
                        <input 
                          type="number" 
                          min="0" 
                          step="1"
                          className="form-control" 
                          value={note500} 
                          onChange={(e) => handleIntegerInput(e.target.value, setNote500)}
                        />
                      </td>
                      <td>₹ {((Number(note500)||0)*500).toLocaleString('en-IN')}</td>
                    </tr>
                    <tr>
                      <td><b>₹ 200</b></td>
                      <td>
                        <input 
                          type="number" 
                          min="0" 
                          step="1"
                          className="form-control" 
                          value={note200} 
                          onChange={(e) => handleIntegerInput(e.target.value, setNote200)}
                        />
                      </td>
                      <td>₹ {((Number(note200)||0)*200).toLocaleString('en-IN')}</td>
                    </tr>
                    <tr>
                      <td><b>₹ 100</b></td>
                      <td>
                        <input 
                          type="number" 
                          min="0" 
                          step="1"
                          className="form-control" 
                          value={note100} 
                          onChange={(e) => handleIntegerInput(e.target.value, setNote100)}
                        />
                      </td>
                      <td>₹ {((Number(note100)||0)*100).toLocaleString('en-IN')}</td>
                    </tr>
                    <tr>
                      <td><b>₹ 50</b></td>
                      <td>
                        <input 
                          type="number" 
                          min="0" 
                          step="1"
                          className="form-control" 
                          value={note50} 
                          onChange={(e) => handleIntegerInput(e.target.value, setNote50)}
                        />
                      </td>
                      <td>₹ {((Number(note50)||0)*50).toLocaleString('en-IN')}</td>
                    </tr>
                    <tr>
                      <td><b>₹ 20</b></td>
                      <td>
                        <input 
                          type="number" 
                          min="0" 
                          step="1"
                          className="form-control" 
                          value={note20} 
                          onChange={(e) => handleIntegerInput(e.target.value, setNote20)}
                        />
                      </td>
                      <td>₹ {((Number(note20)||0)*20).toLocaleString('en-IN')}</td>
                    </tr>
                    <tr>
                      <td><b>₹ 10</b></td>
                      <td>
                        <input 
                          type="number" 
                          min="0" 
                          step="1"
                          className="form-control" 
                          value={note10} 
                          onChange={(e) => handleIntegerInput(e.target.value, setNote10)}
                        />
                      </td>
                      <td>₹ {((Number(note10)||0)*10).toLocaleString('en-IN')}</td>
                    </tr>
                    <tr>
                      <td><b>₹ Coins</b></td>
                      <td>
                        <input 
                          type="number" 
                          min="0" 
                          step="1"
                          className="form-control" 
                          value={coins} 
                          onChange={(e) => handleIntegerInput(e.target.value, setCoins)}
                        />
                      </td>
                      <td>₹ {((Number(coins)||0)*1).toLocaleString('en-IN')}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row mt-3 g-3">
        <div className="col-12 col-md-6 mb-3">
          <div className="card border-info">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <span className="text-muted">Required Amount (Today's Total)</span>
                <h4 className="text-info mb-0">₹ {requiredAmount.toLocaleString('en-IN')}</h4>
              </div>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-6 mb-5" style={{marginBottom:'100px'}}>
          <div className={`card ${totalCash === requiredAmount ? 'border-success' : totalCash > requiredAmount ? 'border-warning' : 'border-danger'}`}>
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <span className="text-muted">Total Cash Collection</span>
                <h4 className={`mb-0 ${totalCash === requiredAmount ? 'text-success' : totalCash > requiredAmount ? 'text-warning' : 'text-danger'}`}>
                  ₹ {totalCash.toLocaleString('en-IN')}
                </h4>
              </div>
              {requiredAmount > 0 && totalCash !== requiredAmount && (
                <div className="mt-2">
                  <small className={totalCash > requiredAmount ? 'text-warning' : 'text-danger'}>
                    {totalCash > requiredAmount 
                      ? `Excess: ₹ ${(totalCash - requiredAmount).toLocaleString('en-IN')}` 
                      : `Short: ₹ ${(requiredAmount - totalCash).toLocaleString('en-IN')}`}
                  </small>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* <Footer /> */}
      <Sidebar />
    </div>
  );
}

export default DenominationCal;


